import {
  DEVICE_NAMES,
  PRODUCT_CATEGORY_NAME,
  PRODUCT_CATEGORY_NAME_MAP,
  VIRTUAL_DEVICES,
} from 'src/features/order/tech-debt/product-details-extra';
import { AcquiringChannel, Catalogue, CatalogueItem, PriceType, Solution } from '../types';
import { PaymentMethodCode } from '@vfs-digital-channels/ns-utils';

export function getCatalogueItemFromCatalogue(
  catalogue: Catalogue,
  productId: string,
  acquiringChannel: AcquiringChannel,
): CatalogueItem | undefined {
  return catalogue.products.find(
    (product) => product.device.id === productId && acquiringChannel === acquiringChannel,
  );
}

export function getCatalogueItemFromCatalogueByTitle(
  catalogue: Catalogue,
  title: string,
  acquiringChannel: AcquiringChannel,
): CatalogueItem | undefined {
  return catalogue.products.find(
    (product) => product.device.title === title && acquiringChannel === acquiringChannel,
  );
}

export const isTenderType = (device: CatalogueItem['device']) => {
  return (
    Object.values(PRODUCT_CATEGORY_NAME_MAP).some(
      (productCategory) => productCategory === device.productCategoryName,
    ) && !device.isProductCategory
  );
};

export const isECommerceInCatalogue = (catalogue: Catalogue, solutions: Solution[]): boolean => {
  const eCommerceCatalogItems = catalogue.products
    .filter((product) => Object.keys(PRODUCT_CATEGORY_NAME_MAP).includes(product.device.title))
    .map((p) => p.device.id);

  return solutions.some((solution) => eCommerceCatalogItems.includes(solution.productId));
};

export const isDeviceInCart = (catalogue: Catalogue, deviceName: string) => {
  return catalogue.products.some(
    (product) => product.device.quantity > 0 && product.device.title === deviceName,
  );
};

export const getTotalProducts = (catalogue: Catalogue) =>
  catalogue.products.reduce<number>((currentTotal, product) => {
    return product.device.quantity ? currentTotal + product.device.quantity : currentTotal;
  }, 0);

export const isOnlyDeviceSelected = (catalogue: Catalogue, deviceName: string): boolean => {
  const basket = catalogue.products.filter(
    ({ device }) =>
      device.quantity > 0 &&
      (device.productCategoryName !== PRODUCT_CATEGORY_NAME ||
        device.title === DEVICE_NAMES.VODAPAY_ECOM_GATEWAY),
  );

  return basket.length === 1 && basket[0].device.title === deviceName;
};

export function requiresPayment(catalogue: Catalogue) {
  return catalogue.products.some(product => product.price.priceType === PriceType.ONCE_OFF && product.device.quantity > 0);
}

export const isOnlyDevicesSelected = (catalogue: Catalogue, deviceNames: string[]): boolean => {
  const foundItems = catalogue.products.filter(
    ({ device }) => device.quantity > 0 && deviceNames.includes(device.title),
  );
  const cartItems = catalogue.products.filter(
    ({ device }) => device.quantity > 0 && !isTenderType(device),
  );

  if (foundItems.length === cartItems.length) {
    return true;
  }
  if (foundItems.length > 1 || cartItems.length > deviceNames.length) {
    return false;
  }

  return false;
};

export const isDeviceSelected = (catalogue: Catalogue, deviceNames: string[]): boolean => {
  return !!catalogue?.products.find(
    ({ device }) => device.quantity > 0 && deviceNames.includes(device.title),
  );
};

export function isVirtualDevice(title: string): boolean {
  return VIRTUAL_DEVICES.includes(title);
}

export function getPriceType(paymentMethodCode?: PaymentMethodCode): PriceType {
  switch (paymentMethodCode) {
    case PaymentMethodCode.ZERO:
      return PriceType.FREE;
    case PaymentMethodCode.CASH:
    case PaymentMethodCode.ONLINE_PAYMENT:
    case PaymentMethodCode.PAYMENT_LINK:
      return PriceType.ONCE_OFF;
    case PaymentMethodCode.RENTAL:
      return PriceType.MONTHLY;

    default:
      return PriceType.ONCE_OFF;
  }
}